import type { CommandOutput, CommandRegistry, CommandContext } from '../types/commands'
import { getPathSuggestions, isDirectory } from './filesystem'
import { levenshteinDistance } from '../utils/stringUtils'

export class CommandParser {
  private commands: CommandRegistry
  // Commands that accept path arguments
  private pathCommands = ['ls', 'cat', 'cd']

  constructor(commands: CommandRegistry) {
    this.commands = commands
  }

  /**
   * Parse and execute a command string
   */
  async execute(input: string, context?: CommandContext): Promise<CommandOutput[]> {
    const trimmed = input.trim()

    if (!trimmed) {
      return []
    }

    // Parse command and arguments
    const parts = this.parseInput(trimmed)
    const commandName = parts[0].toLowerCase()
    const args = parts.slice(1)

    // Find command (including aliases)
    const command = this.findCommand(commandName)

    if (!command) {
      // Find similar commands for "Did you mean...?" suggestion
      const suggestions = this.findSimilarCommands(commandName)

      let errorMessage = `Command not found: ${commandName}\n`

      if (suggestions.length > 0) {
        errorMessage += `\nDid you mean:\n${suggestions.map(s => `  • ${s}`).join('\n')}\n`
      }

      errorMessage += `\nType 'help' for all available commands.`

      return [
        {
          type: 'error',
          content: errorMessage,
        },
      ]
    }

    try {
      const result = await command.execute(args, context)
      return result
    } catch (error) {
      return [
        {
          type: 'error',
          content: `Error executing command: ${error instanceof Error ? error.message : 'Unknown error'}`,
        },
      ]
    }
  }

  /**
   * Parse input string into command and arguments
   * Handles quoted strings as single arguments
   */
  private parseInput(input: string): string[] {
    const parts: string[] = []
    let current = ''
    let inQuotes = false
    let quoteChar = ''

    for (let i = 0; i < input.length; i++) {
      const char = input[i]

      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true
        quoteChar = char
      } else if (char === quoteChar && inQuotes) {
        inQuotes = false
        quoteChar = ''
      } else if (char === ' ' && !inQuotes) {
        if (current) {
          parts.push(current)
          current = ''
        }
      } else {
        current += char
      }
    }

    if (current) {
      parts.push(current)
    }

    return parts
  }

  /**
   * Find command by name or alias
   */
  private findCommand(name: string): any {
    // Direct match
    if (this.commands[name]) {
      return this.commands[name]
    }

    // Check aliases
    for (const cmd of Object.values(this.commands)) {
      if (cmd.aliases?.includes(name)) {
        return cmd
      }
    }

    return null
  }

  /**
   * Find similar commands based on string similarity
   * Returns up to 3 most similar commands
   */
  private findSimilarCommands(input: string): string[] {
    const allCommands: string[] = []

    // Collect all command names and aliases
    for (const [name, cmd] of Object.entries(this.commands)) {
      if (!cmd.hidden) {
        allCommands.push(name)
        if (cmd.aliases) {
          allCommands.push(...cmd.aliases)
        }
      }
    }

    // Calculate similarity scores
    const scores = allCommands.map(cmd => ({
      command: cmd,
      distance: levenshteinDistance(input.toLowerCase(), cmd.toLowerCase()),
    }))

    // Sort by distance (lower is more similar)
    scores.sort((a, b) => a.distance - b.distance)

    // Return top 3 suggestions with distance <= 3 (reasonable typo range)
    return scores
      .filter(s => s.distance <= 3 && s.distance > 0)
      .slice(0, 3)
      .map(s => s.command)
  }

  /**
   * Get all commands (optionally excluding hidden ones)
   */
  getCommands(includeHidden: boolean = false): CommandRegistry {
    if (includeHidden) {
      return this.commands
    }

    const visible: CommandRegistry = {}
    for (const [name, cmd] of Object.entries(this.commands)) {
      if (!cmd.hidden) {
        visible[name] = cmd
      }
    }
    return visible
  }

  /**
   * Get autocomplete suggestions for partial input
   * @param partial - The partial input to complete
   * @param currentDirectory - The current working directory (default: '~')
   */
  getAutocomplete(partial: string, currentDirectory: string = '~'): string[] {
    const trimmed = partial.trim()

    // Check if we're completing a path (contains space, indicating we're past the command)
    const spaceIndex = trimmed.indexOf(' ')

    if (spaceIndex > 0) {
      // We're autocompleting an argument (likely a path)
      const commandPart = trimmed.substring(0, spaceIndex).toLowerCase()
      const argsPart = trimmed.substring(spaceIndex + 1)

      // Find the command to check its preferences
      const command = this.findCommand(commandPart)

      // Only autocomplete paths for commands that accept them
      if (command && this.pathCommands.includes(commandPart)) {
        // Check if -a flag is present (for showing hidden files)
        const showHidden = argsPart.includes('-a')

        // Extract the path part (remove flags)
        const pathPart = argsPart.replace(/-a\s*/g, '').trim()

        // Get path suggestions with current directory context
        const pathSuggestions = getPathSuggestions(pathPart, currentDirectory, showHidden)

        // Filter based on command preferences
        const acceptsFiles = command.acceptsFiles !== false // Default true if not specified
        const acceptsDirectories = command.acceptsDirectories !== false // Default true if not specified

        const filteredSuggestions = pathSuggestions.filter((path) => {
          const isDirPath = isDirectory(path)
          if (isDirPath) {
            return acceptsDirectories
          } else {
            return acceptsFiles
          }
        })

        // Return full command + path suggestions
        return filteredSuggestions.map((path) => {
          // Add trailing slash for directories
          const displayPath = isDirectory(path) ? path + '/' : path
          // Include -a flag if it was in the original input
          const flagPart = showHidden ? '-a ' : ''
          return `${commandPart} ${flagPart}${displayPath}`
        })
      }

      return []
    }

    // Autocomplete command names
    const lower = trimmed.toLowerCase()
    const suggestions: string[] = []

    for (const [name, cmd] of Object.entries(this.commands)) {
      if (name.startsWith(lower) && !cmd.hidden) {
        suggestions.push(name)
      }

      // Check aliases
      if (cmd.aliases) {
        for (const alias of cmd.aliases) {
          if (alias.startsWith(lower) && !cmd.hidden) {
            suggestions.push(alias)
          }
        }
      }
    }

    return suggestions.sort()
  }
}
